import OdfjellTooltip from 'src/components/ui/odfjellTooltip/OdfjellTooltip';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useStowageUIStore } from 'src/context/StowageUIContext';
import { useEffect, useState } from 'react';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useAuthStore } from 'src/context/AuthContext';
import { MenuItemButton } from '../../PageHeader.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useViolationsStore } from 'src/context/ViolationsContext';
import PageHeaderStowagePublishPromotePanel from '../pageHeaderStowagePublishPromotePanel/PageHeaderStowagePublishPromotePanel';
import { StowageRuleViolationStatus } from 'src/enums/stowage/stowageRule/stowageRuleViolationStatus';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faShareFromSquare, faShip } from '@fortawesome/free-solid-svg-icons';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { RedTriangle, YellowTriangle } from 'src/components/stowage/tankPlanView/TankPlanViewer.styles';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { getViolationGroups } from 'src/utils/stowage/violations/violationGroups';
import { useVesselStore } from 'src/context/VesselContext';

export type ButtonActionType = 'publish' | 'promote';

// Button configuration for easy maintenance
const BUTTON_CONFIG: Record<ButtonActionType, { icon: IconDefinition; text: string }> = {
    publish: { icon: faShareFromSquare, text: 'Publish' },
    promote: { icon: faShip, text: 'Promote' },
};

// Requirements:
// - The buttons should only be displayed if the user has the permission to publish or promote. If the user has both pemissions, the 2 buttons should be displayed.
// - The publish button should be enabled if the stowage plan is a draft and the user has the permission to publish.
// - The promote button should be enabled if the stowage plan is a draft or published and the user has the permission to promote.
// - The promote button should be disabled if the stowage plan is not valid.
// - The publish and promote buttons should be disabled if there are any new violations.
const PageHeaderStowagePublishPromoteButton: React.FC = () => {
    const { activeStowagePlan } = useStowagePlanStore();
    const { isPublishPromoteOpen, setIsPublishPromoteOpen } = useStowageUIStore();
    const { isStowagePlanValid, violations } = useViolationsStore();
    const { role } = useAuthStore();
    const { isOffline } = useConnectionStore();
    const { selectedVessel } = useVesselStore();
    const [selectedAction, setSelectedAction] = useState<ButtonActionType | null>(null);
    const [numHardRuleViolations, setNumHardRuleViolations] = useState<number | null>(null);
    const [numSoftRuleViolations, setNumSoftRuleViolations] = useState<number | null>(null);

    const isAuthorizedPromote = hasPermission(role, stowageFeatureEnum.Promote);
    const isAuthorizedPublish = hasPermission(role, stowageFeatureEnum.Publish);

    const hasNewViolations = violations?.some((violation) => violation.status === StowageRuleViolationStatus.New) || false;
    const hasTankAdjacencyConfigurationErrors = (selectedVessel?.tankAdjacencyConfigurationErrors.length ?? 0) > 0;

    const canShowPublishButton = isAuthorizedPublish && activeStowagePlan?.type === StowagePlanTypeEnum.Draft;
    const canShowPromoteButton =
        isAuthorizedPromote && (activeStowagePlan?.type === StowagePlanTypeEnum.Draft || activeStowagePlan?.type === StowagePlanTypeEnum.Published);

    const isPublishEnabled = canShowPublishButton && !hasNewViolations && !hasTankAdjacencyConfigurationErrors;
    const isPromoteEnabled = canShowPromoteButton && !hasNewViolations && isStowagePlanValid && !isOffline && !hasTankAdjacencyConfigurationErrors;

    useEffect(() => {
        const _hardRuleViolationsCount = violations?.filter((o) => o.type === StowageRuleConsequenceType.HardRule).length || 0;
        const _softRuleViolationsCount = getViolationGroups(violations).filter(
            (group) => group.members[0].type === StowageRuleConsequenceType.SoftRule && group.status !== 'Accepted'
        ).length;
        setNumHardRuleViolations(_hardRuleViolationsCount);
        setNumSoftRuleViolations(_softRuleViolationsCount);
    }, [violations]);

    const getDisabledReason = (actionType: ButtonActionType): string => {
        const isEnabled = actionType === 'publish' ? isPublishEnabled : isPromoteEnabled;
        if (isEnabled) return '';

        if (hasTankAdjacencyConfigurationErrors) {
            return `Cannot ${actionType} while vessel tank-adjacency data is invalid. Contact Armada Portal support to correct the vessel configuration.`;
        }

        if (actionType === 'promote') {
            if (isOffline) {
                return 'Promote is not available when offline';
            }
            if (!isStowagePlanValid) {
                return 'Stowage plan is not valid';
            }
        }

        if (hasNewViolations) {
            return `Soft rule violations must be accepted before you can ${actionType} this plan`;
        }

        return '';
    };

    const handleButtonClick = (actionType: ButtonActionType): void => {
        setSelectedAction(actionType);
        setIsPublishPromoteOpen(true);
    };

    const getViolationsIcon = (actionType: ButtonActionType, title: string) => {
        // For publish action, show soft rule violations icon
        if (actionType === 'publish' && numSoftRuleViolations && numSoftRuleViolations > 0) {
            return (
                <YellowTriangle className="soft-rule-warning-icon" title={title}>
                    <FontAwesomeIcon icon="exclamation-triangle" />
                </YellowTriangle>
            );
        }

        // For promote action, prioritize soft rule violations icon
        if (actionType === 'promote') {
            if (numSoftRuleViolations && numSoftRuleViolations > 0) {
                return (
                    <YellowTriangle className="soft-rule-warning-icon" title={title}>
                        <FontAwesomeIcon icon="exclamation-triangle" />
                    </YellowTriangle>
                );
            } else if (numHardRuleViolations && numHardRuleViolations > 0) {
                return (
                    <RedTriangle className="hard-rule-warning-icon" title={title}>
                        <FontAwesomeIcon icon="exclamation-triangle" />
                    </RedTriangle>
                );
            }
        }

        return null;
    };

    const getViolationsTitle = (actionType: ButtonActionType): string => {
        if (actionType === 'publish' && numSoftRuleViolations && numSoftRuleViolations > 0) {
            return 'Soft Rule Violations';
        } else if (actionType === 'promote') {
            if (numSoftRuleViolations && numSoftRuleViolations > 0) {
                return 'Soft Rule Violations';
            } else if (numHardRuleViolations && numHardRuleViolations > 0) {
                return 'Hard Rule Violations';
            }
        }
        return '';
    };

    const getViolationsTooltip = (actionType: ButtonActionType): React.ReactNode => {
        let description = '';

        if (actionType === 'publish' && numSoftRuleViolations && numSoftRuleViolations > 0) {
            const text = numSoftRuleViolations === 1 ? 'violation' : 'violations';
            description = `${numSoftRuleViolations} soft rule ${text} present. 
            All violations must be reviewed and accepted before publishing this plan.`;
        } else if (actionType === 'promote') {
            // First check for soft rule violations
            if (numSoftRuleViolations && numSoftRuleViolations > 0) {
                const text = numSoftRuleViolations === 1 ? 'violation' : 'violations';
                description = `${numSoftRuleViolations} soft rule ${text} present. 
                All soft rule violations must be reviewed and accepted before promoting this plan.`;
            }
            // If no soft rule violations, check for hard rule violations
            else if (numHardRuleViolations && numHardRuleViolations > 0) {
                const text = numHardRuleViolations === 1 ? 'violation' : 'violations';
                description = `${numHardRuleViolations} hard rule ${text} present. 
                Hard rule violations must be resolved before promoting this plan.`;
            }
        }

        return description ? <span>{description}</span> : null;
    };

    const renderMenuButton = (actionType: ButtonActionType) => {
        const { icon, text } = BUTTON_CONFIG[actionType];
        const disabledReason = getDisabledReason(actionType);
        const isEnabled = actionType === 'publish' ? isPublishEnabled : isPromoteEnabled;
        const tooltipContent = getViolationsTooltip(actionType);
        const tooltipTitle = getViolationsTitle(actionType);

        const buttonContent = (
            <>
                <FontAwesomeIcon icon={icon} className="icon" />
                <span className="icon-text">{text}</span>
                {getViolationsIcon(actionType, disabledReason)}
            </>
        );

        return (
            <div key={actionType}>
                {tooltipContent ? (
                    <OdfjellTooltip content={tooltipContent} title={tooltipTitle} position="bottom" maxWidth="300px" minWidth="200px">
                        <MenuItemButton onClick={() => isEnabled && handleButtonClick(actionType)} isExtraWide={true} isDisabled={!isEnabled}>
                            {buttonContent}
                        </MenuItemButton>
                    </OdfjellTooltip>
                ) : (
                    <MenuItemButton
                        onClick={() => isEnabled && handleButtonClick(actionType)}
                        title={disabledReason}
                        isExtraWide={true}
                        isDisabled={!isEnabled}
                    >
                        {buttonContent}
                    </MenuItemButton>
                )}
            </div>
        );
    };

    if (!activeStowagePlan || (!canShowPublishButton && !canShowPromoteButton)) {
        return null;
    }

    return (
        <>
            {isPublishPromoteOpen && selectedAction && <PageHeaderStowagePublishPromotePanel type={selectedAction} />}

            {canShowPublishButton && renderMenuButton('publish')}
            {canShowPromoteButton && renderMenuButton('promote')}
        </>
    );
};

export default PageHeaderStowagePublishPromoteButton;
